import { cloneDeep, debounce as _debounce, get, isEqual, unset } from 'lodash-es'
import { computed, type MaybeRef, reactive, ref, unref, watch } from 'vue'

import { type MaybeArray, type MaybePromise, type VFormTrigger } from '../types.ts'
import { configVForm as config } from '../utils/config.ts'
import { isEmpty } from '../utils/helpers.ts'
import { RequestError, type RequestMethod } from '../utils/httpClient.ts'
import { validateRule } from '../utils/validation.ts'

export type Form<TData extends Record<string, any>> = ReturnType<typeof useForm<TData>>

type URL = string | (() => string)

// DecrementMap, Decrement, NestedPath, PathValue are all utility types created by ChatGPT.
// They are used in the useForm function to validate form data and rules in a nested way.
interface DecrementMap {
  10: 9
  9: 8
  8: 7
  7: 6
  6: 5
  5: 4
  4: 3
  3: 2
  2: 1
  1: 0
  0: 0
}

type Decrement<D extends number> = D extends keyof DecrementMap ? DecrementMap[D] : 0

type NestedPath<T, Depth extends number = 10> = [Depth] extends [0]
  ? never
  : T extends any[]
    ? `${number}` | `${number}.${NestedPath<T[number], Decrement<Depth>>}`
    : T extends object
      ? {
          [K in keyof T & string]: T[K] extends object ? `${K}` | `${K}.${NestedPath<T[K], Decrement<Depth>>}` : `${K}`
        }[keyof T & string]
      : never

type PathValue<T, P extends string, Depth extends number = 10> = [Depth] extends [0]
  ? never
  : P extends `${infer K}.${infer Rest}`
    ? K extends keyof T
      ? PathValue<T[K], Rest, Decrement<Depth>>
      : T extends any[]
        ? K extends `${number}`
          ? PathValue<T[number], Rest, Decrement<Depth>>
          : never
        : never
    : P extends keyof T
      ? T[P]
      : T extends any[]
        ? P extends `${number}`
          ? T[number]
          : never
        : never

export interface FormErrors {
  [key: string | number | symbol]: string[] | FormErrors
}

export interface FormRule<TValue = any, TForm extends Record<string, any> = Record<string, any>> {
  required?: boolean
  match?: string
  mismatch?: string
  min?: number
  max?: number
  type?: string
  in?: string | string[]
  lt?: number | string
  gt?: number | string
  lte?: number | string
  gte?: number | string
  gtLabel?: string
  gteLabel?: string
  ltLabel?: string
  lteLabel?: string
  message?: string
  validator?: FormValidator<TValue, TForm>
  validators?: {
    validator: FormValidator<TValue, TForm>
    message: string
  }[]
}

export type FormValidator<TValue, TForm extends Record<string, any>> = (value: TValue, form: TForm) => MaybePromise<FormValidatorResponse | boolean>

export interface FormValidatorResponse {
  message: string
}

// The rules object is a nested object where the keys are the paths to the properties in the form data.
// The values are either a single rule or an array of rules.
export type Rules<TForm extends Record<string, any> = Record<string, any>> = Partial<{
  // @ts-expect-error - TODO: Fix this.
  [K in keyof TForm | NestedPath<TForm>]: MaybeArray<FormRule<PathValue<TForm, K>, TForm>>
}>

interface Options<TData extends Record<string, any>> {
  rules?: MaybeRef<Rules<TData>>
  validateOn?: VFormTrigger
  debounce?: number
  precognition?: boolean
}

function getUrl(url: URL): string {
  return typeof url === 'string' ? url : url()
}

/**
 *
 * @param method The HTTP method to use when submitting the form.
 * @param url The url to use when submitting the form.
 * @param inputs The original form data.
 */
export function useForm<TData extends Record<string, any>>(method: RequestMethod | TData, url?: URL | Options<TData>, inputs?: TData) {
  const data = (typeof method === 'string' ? inputs : method) as TData

  const options: Options<TData> = typeof url === 'object' ? url : {}
  if (typeof url === 'object') {
    url = undefined
  }

  /**
   * The original data, used for resets and checking if there are any changes.
   */
  const $defaults = cloneDeep(data)

  /**
   * The previous data, used for checking if there were changes since the last check.
   */
  let previousData = cloneDeep($defaults)

  const validating = ref(false)
  const submitting = ref(false)
  const loading = computed(() => validating.value || submitting.value)
  const hasChanges = ref(false)
  const hasErrors = computed((): boolean => !isEmpty(form.$errors))
  const changedKeys = computed((): (keyof TData)[] => Object.keys($defaults).filter((key) => !isEqual(form[key], $defaults[key])))

  const additionalRules = ref<MaybeRef<Rules<TData>>>({})

  const rules = computed((): Rules<TData> => {
    const rules: Rules<TData> = Object.assign({}, unref(options.rules ?? {}))

    for (const [prop, rule] of Object.entries(unref(additionalRules.value))) {
      // @ts-expect-error - `rules` should have the same keys as `additionalRules`.
      rules[prop] = [rules[prop] ?? [], rule].flat()
    }

    return rules
  })

  const form = reactive({
    ...data,

    $precognition: !!(typeof method === 'string' && url) || (options.precognition ?? false),
    $debounce: options.debounce,
    $defaults,
    $rules: rules,
    $errors: {} as FormErrors,
    $validateOn: options.validateOn,
    $hasChanges: hasChanges,
    $hasErrors: hasErrors,
    $validating: validating,
    $submitting: submitting,
    $loading: loading,
    $changedKeys: changedKeys,

    data(): TData {
      const data: Record<string, unknown> = {}

      for (const key in $defaults) {
        data[key] = form[key]
      }

      return cloneDeep(data) as TData
    },

    toJSON(): TData {
      return form.data()
    },

    reset(...fields: (keyof Partial<TData>)[]): Form<TData> {
      const original = cloneDeep($defaults)

      if (fields.length === 0) {
        Object.assign(form, original)
      } else {
        for (const key of fields) {
          // @ts-expect-error - `original` and `form` should have the same keys.
          form[key] = original[key]
        }
      }

      form.$errors = {}

      return form
    },

    updateDefaults(defaults?: Partial<TData>): void {
      if (!defaults) {
        Object.assign($defaults, form.data())

        return
      }

      Object.assign($defaults, defaults)
      Object.assign(form, cloneDeep(defaults))
    },

    async validate(): Promise<void> {
      if (form.$precognition) {
        previousData = form.data()

        return await form.validateWithPrecognition()
      }

      validating.value = true
      const promises: Promise<void>[] = []

      for (const prop in form.$rules) {
        promises.push(form._validateProp(prop))
      }

      await Promise.all(promises)

      validating.value = false

      if (form.$hasErrors) {
        console.warn('Validation errors: ', JSON.stringify(form.$errors, null, 2))
        throw Error('Validation failed')
      }
    },

    async _validateProp(prop: string): Promise<void> {
      const rules = get(form.$rules, prop)

      const currentData = form.data()
      if (form.$precognition) {
        previousData = currentData
        form.validateWithPrecognition({ only: [prop] })
      }

      // Don't do anything when no rule exists.
      if (!rules) {
        return
      }

      // Clear previous errors.
      unset(form.$errors, prop)

      // Clear empty nested errors.
      const keys = prop.split('.')
      if (keys.length > 1) {
        let errors: FormErrors | string[] = form.$errors
        for (const key of keys) {
          if (Array.isArray(errors)) {
            break
          }

          const nestedErrors: FormErrors | string[] = errors[key]
          if (!nestedErrors || isEmpty(nestedErrors)) {
            delete errors[key]
            break
          } else {
            errors = nestedErrors
          }
        }
      }

      if (Array.isArray(rules)) {
        await Promise.all(rules.map((rule) => form._validateRuleAndSetErrors({ rule, prop })))
      } else {
        await form._validateRuleAndSetErrors({ rule: rules, prop })
      }
    },

    async _validateRuleAndSetErrors({ rule, prop }: { rule: FormRule<TData[string], TData>; prop: string }): Promise<void> {
      // @ts-expect-error - `rule` should be a valid rule.
      for await (const error of validateRule({ rule, prop, form })) {
        form._setError(error, prop)
      }
    },

    async validateWithPrecognition({ onlyChanged, only }: { onlyChanged?: boolean; only?: string[] } = {}): Promise<void> {
      if (typeof method !== 'string' || !url) {
        throw Error('You need to provide a method and url in `useForm()` to be able to validate with precognition.')
      }

      validating.value = true
      let validatedKeys = Object.keys($defaults)
      const headers: Record<string, any> = { Precognition: true }
      if (only) {
        if (only.length === 0) {
          return
        }
        validatedKeys = only
        headers['Precognition-Validate-Only'] = validatedKeys.join()
      } else if (onlyChanged) {
        validatedKeys = changedKeys.value as string[]
        if (validatedKeys.length > 0) {
          headers['Precognition-Validate-Only'] = validatedKeys.join()
        } else {
          return
        }
      }

      const data: Record<string, unknown> = {}
      for (const key of validatedKeys) {
        data[key] = form[key]
      }

      try {
        await config.httpClient.request({ method, url: getUrl(url), headers, data: previousData })

        // If Precognition was successful, we can reset all errors.
        if (onlyChanged || only?.length) {
          for (const key of validatedKeys) {
            delete form.$errors[key]
          }
        } else {
          form.$errors = {}
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          validating.value = false

          return
        }
        if (!(error instanceof RequestError) || error.response?.status !== 422) {
          validating.value = false
          throw error
        }

        const data = error.response.data

        for (const key of validatedKeys) {
          if (key in data.errors) {
            form.$errors[key] = data.errors[key]
          } else {
            delete form.$errors[key]
          }
        }

        validating.value = false
        throw error
      }

      validating.value = false
    },

    _setError(error: string, prop: string): void {
      const errors: FormErrors | string[] = get(form.$errors, prop, [])
      if (Array.isArray(errors) && !errors.includes(error)) {
        errors.push(error)

        form.$errors[prop] = errors
      }
    },

    async submit<TResponseData = any>(): Promise<{ data: TResponseData }> {
      await form.validate()

      if (typeof method !== 'string' || !url) {
        throw Error('You need to provide a method and url in `useForm()` to be able to submit the form.')
      }

      submitting.value = true
      const response = config.httpClient.request<TResponseData>({ method, url: getUrl(url), data: form.data() })
      response.catch((error) => {
        if (!(error instanceof RequestError) || error.response?.status !== 422) {
          return
        }

        const data = error.response.data

        for (const key in $defaults) {
          if (key in data.errors) {
            form.$errors[key] = data.errors[key]
          } else {
            delete form.$errors[key]
          }
        }
      })
      response.finally(() => (submitting.value = false))

      return response
    },

    addRules(rules: Rules): void {
      for (const [prop, rule] of Object.entries(rules)) {
        additionalRules.value[prop] = [additionalRules.value[prop] ?? [], rule].flat()
      }
    },

    replaceRules(rules: MaybeRef<Rules<TData>>): void {
      options.rules = rules
      additionalRules.value = {}
    },
  })

  // Because comparing the data can be expensive, we debounce this operation.
  const debounceUpdateHasChanges = _debounce(
    () => {
      const currentData = form.data()
      hasChanges.value = !isEqual($defaults, currentData)
    },
    300,
    { leading: true, trailing: true },
  )

  watch(form, () => debounceUpdateHasChanges(), { deep: true })

  return form
}
